import { useUser } from '../context/userContext';

/**
 * Custom hook for checking user permissions
 * Returns helper functions to check if user can view or edit specific areas
 */
export const usePermissions = () => {
  const { permissions } = useUser();

  /**
   * Check if user has access to view an area
   * @param {string} area - The permission area (e.g., 'chemicals', 'dispatch', 'production')
   * @returns {boolean} - True if user can view the area
   */
  const canView = (area) => {
    if (!permissions || !permissions[area]) {
      return false;
    }

    const perm = permissions[area];
    return perm.access_level === 'full' || perm.access_level === 'limited';
  };

  /**
   * Check if user has permission to edit an area
   * @param {string} area - The permission area (e.g., 'chemicals', 'dispatch', 'production')
   * @returns {boolean} - True if user can edit the area
   */
  const canEdit = (area) => {
    if (!permissions || !permissions[area]) {
      return false;
    }

    const perm = permissions[area];
    const hasAccess = perm.access_level === 'full' || perm.access_level === 'limited';
    const canEditFlag = perm.can_edit !== false; // Default to true if undefined

    return hasAccess && canEditFlag;
  };

  /**
   * Check if user is in demo/view-only mode
   * @returns {boolean} - True if user is a demo viewer
   */
  const isDemoViewer = () => {
    // Check if all areas have can_edit: false
    if (!permissions || Object.keys(permissions).length === 0) {
      return false;
    }

    // If at least one permission has can_edit: true, they're not a demo viewer
    for (const area in permissions) {
      if (permissions[area].can_edit !== false) {
        return false;
      }
    }

    return true;
  };

  /**
   * Check if user can approve data
   * @returns {boolean} - True if user has approval permissions
   */
  const canApprove = () => {
    return canEdit('approval');
  };

  return {
    canView,
    canEdit,
    isDemoViewer,
    canApprove,
    permissions
  };
};
